import { useCallback, useEffect, useMemo, useState } from "react";
import type { RegistrationService } from "@/domain/port/RegistrationService";

type RegistrationCancelUiState =
  | {
      status: "loading";
      title: string;
      message: string;
    }
  | {
      status: "checkoutOpen";
      title: string;
      message: string;
    }
  | {
      status: "startingCheckout";
      title: string;
      message: string;
    }
  | {
      status: "redirectingToCheckout";
      title: string;
      message: string;
    }
  | {
      status: "cancelingCheckout";
      title: string;
      message: string;
    }
  | {
      status: "processing";
      title: string;
      message: string;
    }
  | {
      status: "completed";
      title: string;
      message: string;
    }
  | {
      status: "error";
      title: string;
      message: string;
      canRetry: boolean;
    };

type UseRegistrationCancelViewModelArgs = {
  registrationId: string | undefined;
  registrationService: RegistrationService;
};

type RegistrationCancelViewModel = {
  state: {
    uiState: RegistrationCancelUiState;
    isBusy: boolean;
    canActOnCheckout: boolean;
  };
  intents: {
    refresh: () => void;
    resumeCheckout: () => void;
    cancelCheckout: () => void;
    goToLandingPage: () => void;
  };
};

export function useRegistrationCancelViewModel({
  registrationId,
  registrationService,
}: UseRegistrationCancelViewModelArgs): RegistrationCancelViewModel {
  const [uiState, setUiState] = useState<RegistrationCancelUiState>({
    status: "loading",
    title: "Checkout wird geladen",
    message: "Bitte warten…",
  });

  const load = useCallback(async () => {
    if (!registrationId) {
      setUiState({
        status: "error",
        title: "Registrierung nicht gefunden",
        message: "Es wurde keine gültige Registrierungs-ID gefunden.",
        canRetry: false,
      });
      return;
    }

    try {
      const result =
        await registrationService.getRegistrationStatus(registrationId);

      switch (result.status) {
        case "CHECKOUT_CREATED":
          setUiState({
            status: "checkoutOpen",
            title: "Bezahlung wurde nicht abgeschlossen",
            message:
              "Sie können die Bezahlung fortsetzen oder den Bezahlvorgang abbrechen.",
          });
          return;

        case "CHECKOUT_COMPLETED":
        case "ACTIVATING":
          setUiState({
            status: "processing",
            title: "Registrierung wird verarbeitet",
            message:
              "Ihre Bezahlung wurde erkannt. Ihr Konto wird gerade eingerichtet.",
          });
          return;

        case "ACTIVE":
          setUiState({
            status: "completed",
            title: "Registrierung bereits abgeschlossen",
            message: "Ihr Konto wurde bereits erfolgreich erstellt.",
          });
          return;

        case "EXPIRED":
          setUiState({
            status: "error",
            title: "Checkout abgelaufen",
            message:
              result.message ??
              "Der Checkout ist abgelaufen. Bitte starten Sie die Registrierung erneut.",
            canRetry: false,
          });
          return;

        case "CANCELED":
          setUiState({
            status: "error",
            title: "Bezahlvorgang abgebrochen",
            message:
              result.message ??
              "Der Bezahlvorgang wurde bereits abgebrochen.",
            canRetry: false,
          });
          return;

        case "FAILED":
          setUiState({
            status: "error",
            title: "Registrierung fehlgeschlagen",
            message:
              result.message ??
              "Ihre Registrierung konnte nicht abgeschlossen werden.",
            canRetry: false,
          });
          return;

        case "INITIATED":
          setUiState({
            status: "processing",
            title: "Checkout wird vorbereitet",
            message:
              "Der Checkout wurde noch nicht vollständig vorbereitet. Bitte versuchen Sie es gleich erneut.",
          });
          return;

        default:
          setUiState({
            status: "error",
            title: "Unbekannter Status",
            message:
              "Der Status der Registrierung konnte nicht verarbeitet werden.",
            canRetry: true,
          });
      }
    } catch {
      setUiState({
        status: "error",
        title: "Status konnte nicht geladen werden",
        message:
          "Der Status der Registrierung konnte gerade nicht geladen werden.",
        canRetry: true,
      });
    }
  }, [registrationId, registrationService]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(() => {
    setUiState({
      status: "loading",
      title: "Checkout wird geladen",
      message: "Bitte warten…",
    });

    void load();
  }, [load]);

  const resumeCheckout = useCallback(async () => {
    if (!registrationId) {
      setUiState({
        status: "error",
        title: "Registrierung nicht gefunden",
        message: "Es wurde keine gültige Registrierungs-ID gefunden.",
        canRetry: false,
      });
      return;
    }

    setUiState({
      status: "startingCheckout",
      title: "Checkout wird geöffnet",
      message: "Bitte warten…",
    });

    try {
      const response =
        await registrationService.resumeRegistrationCheckout(registrationId);

      setUiState({
        status: "redirectingToCheckout",
        title: "Weiterleitung zur Bezahlung",
        message: "Sie werden jetzt zum sicheren Checkout weitergeleitet…",
      });

      window.location.assign(response.checkoutUrl);
    } catch {
      setUiState({
        status: "error",
        title: "Checkout konnte nicht geöffnet werden",
        message:
          "Die Bezahlung konnte nicht fortgesetzt werden. Bitte versuchen Sie es erneut.",
        canRetry: true,
      });
    }
  }, [registrationId, registrationService]);

  const cancelCheckout = useCallback(async () => {
    if (!registrationId) {
      window.location.assign("/");
      return;
    }

    setUiState({
      status: "cancelingCheckout",
      title: "Bezahlvorgang wird abgebrochen",
      message: "Bitte warten…",
    });

    try {
      await registrationService.cancelRegistrationCheckout(registrationId);
      window.location.assign("/");
    } catch {
      setUiState({
        status: "error",
        title: "Bezahlvorgang konnte nicht abgebrochen werden",
        message:
          "Der Bezahlvorgang konnte gerade nicht abgebrochen werden. Bitte versuchen Sie es erneut.",
        canRetry: true,
      });
    }
  }, [registrationId, registrationService]);

  const goToLandingPage = useCallback(() => {
    window.location.assign("/");
  }, []);

  const isBusy =
    uiState.status === "loading" ||
    uiState.status === "startingCheckout" ||
    uiState.status === "redirectingToCheckout" ||
    uiState.status === "cancelingCheckout";

  const canActOnCheckout = uiState.status === "checkoutOpen";

  return useMemo(
    () => ({
      state: {
        uiState,
        isBusy,
        canActOnCheckout,
      },
      intents: {
        refresh,
        resumeCheckout,
        cancelCheckout,
        goToLandingPage,
      },
    }),
    [
      canActOnCheckout,
      cancelCheckout,
      goToLandingPage,
      isBusy,
      refresh,
      resumeCheckout,
      uiState,
    ],
  );
}
